import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Loader2, Upload, Download, X, Building2, Phone, MoreHorizontal } from 'lucide-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { usePagination } from '@/hooks/usePagination';
import { getDefaultStatusId } from '@/hooks/useDefaultStatus';
import { vendorService, externalSystemService, profileService, type ServiceContext } from '@/services';
import VendorFormDialog, { VendorFormData } from '@/components/VendorFormDialog';
import VendorImportWizard, { VendorImportRow } from '@/components/VendorImportWizard';
import VendorExportWizard from '@/components/VendorExportWizard';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Vendor {
  id: string;
  name: string;
  city: string | null;
  phone: string | null;
  email: string | null;
  status_id: string | null;
  address: string | null;
  state: string | null;
  country: string | null;
  created: string | null;
  modified: string | null;
  created_by: string | null;
  updated_by: string | null;
  current_status: { id: string; name: string } | null;
  external_system: string | null;
}

interface ExternalSystem {
  id: string;
  name: string;
}

export default function Vendors() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use the pagination hook for server-side pagination with localStorage persistence
  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    resetToFirstPage,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'vendors' });
  
  const [selectedExternalSystem, setSelectedExternalSystem] = useState<string | null>(
    searchParams.get('external_system')
  );
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [allVendors, setAllVendors] = useState<Vendor[]>([]); // For export functionality
  const [totalCount, setTotalCount] = useState(0);
  const [externalSystems, setExternalSystems] = useState<ExternalSystem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<VendorFormData | null>(null);
  const [editingSnapshot, setEditingSnapshot] = useState<Record<string, unknown> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<Vendor | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // Debounced click handling for single vs double click
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const handleRowClick = useCallback((vendor: Vendor) => {
    // If there's already a pending click, clear it (this means it's a double-click)
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
      return;
    }
    
    // Set a timeout for single-click action
    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null;
      handleEditVendor(vendor);
    }, 250);
  }, []);
  
  const handleRowDoubleClick = useCallback((vendor: Vendor) => {
    // Clear any pending single-click timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    // Navigate to vendor detail page
    navigate(`/vendors/${vendor.id}`);
  }, [navigate]);

  // Fetch company ID and external systems on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) return;

      try {
        // Get user's company_id
        const profileResult = await profileService.get(user.id);
        const profile = profileResult.data;

        if (!profile?.company_id) {
          setIsLoading(false);
          return;
        }

        setCompanyId(profile.company_id);

        // Fetch external systems
        const externalSystemsResult = await externalSystemService.list(profile.company_id);
        if (!externalSystemsResult.error) {
          setExternalSystems(externalSystemsResult.data || []);
        }
      } catch (error: any) {
        console.error('Error fetching initial data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load data.',
          variant: 'destructive',
        });
      }
    };

    fetchInitialData();
  }, [user, toast]);

  // Fetch paginated vendors when pagination/filters change
  useEffect(() => {
    const fetchVendors = async () => {
      if (!companyId) return;

      setIsLoading(true);
      try {
        const result = await vendorService.listWithStatusPaginated(
          companyId,
          { page: pageIndex, pageSize },
          { search: debouncedSearch, externalSystem: selectedExternalSystem }
        );

        if (result.error) throw result.error;
        
        setVendors(result.data || []);
        setTotalCount(result.totalCount);
      } catch (error: any) {
        console.error('Error fetching vendors:', error);
        toast({
          title: 'Error',
          description: 'Failed to load vendors.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchVendors();
  }, [companyId, pageIndex, pageSize, debouncedSearch, selectedExternalSystem, toast]);

  // Fetch all vendors when export dialog opens (for export functionality)
  useEffect(() => {
    const fetchAllVendors = async () => {
      if (!exportDialogOpen || !companyId) return;

      try {
        const result = await vendorService.listAllWithStatus(companyId);
        if (!result.error) {
          setAllVendors(result.data || []);
        }
      } catch (error) {
        console.error('Error fetching all vendors for export:', error);
      }
    };

    fetchAllVendors();
  }, [exportDialogOpen, companyId]);

  // Sync external system filter with URL (separate from pagination URL params)
  useEffect(() => {
    const currentParams = new URLSearchParams(searchParams);
    if (selectedExternalSystem) {
      currentParams.set('external_system', selectedExternalSystem);
    } else {
      currentParams.delete('external_system');
    }
    setSearchParams(currentParams, { replace: true });
  }, [selectedExternalSystem]);

  // Reset to first page when external system filter changes
  const handleExternalSystemChange = (value: string) => {
    setSelectedExternalSystem(value === "all" ? null : value);
    resetToFirstPage();
  };

  const hasActiveFilters = searchValue || selectedExternalSystem;

  const clearFilters = () => {
    setSearchValue('');
    setSelectedExternalSystem(null);
    resetToFirstPage();
  };

  const handleAddVendor = () => {
    setEditingVendor(null);
    setEditingSnapshot(null);
    setDialogOpen(true);
  };

  const handleEditVendor = async (vendor: Vendor) => {
    try {
      // Fetch fresh data with joined profile names for audit display
      const result = await vendorService.getWithAuditNames(vendor.id);
      if (result.error) throw result.error;
      if (!result.data) throw new Error('Vendor not found');

      const data = result.data;

      // Capture snapshot for history tracking
      setEditingSnapshot({
        name: data.name,
        city: data.city,
        phone: data.phone,
        email: data.email,
        address: data.address,
        state: data.state,
        country: data.country,
      });

      setEditingVendor({
        id: data.id,
        name: data.name,
        city: data.city || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        state: data.state || '',
        country: data.country || 'United States',
        created: data.created,
        modified: data.modified,
        created_by_profile: data.created_by_profile,
        updated_by_profile: data.updated_by_profile,
      });
      setDialogOpen(true);
    } catch (error: any) {
      console.error('Error fetching vendor:', error);
      toast({
        title: 'Error',
        description: 'Failed to load vendor details.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveVendor = async (data: VendorFormData) => {
    if (!companyId || !user) {
      toast({
        title: 'Error',
        description: 'No company associated with your profile.',
        variant: 'destructive',
      });
      return;
    }

    const context: ServiceContext = {
      companyId,
      userId: user.id,
    };

    try {
      if (data.id) {
        // Update existing vendor using service layer
        const updateData = {
          name: data.name,
          city: data.city || null,
          phone: data.phone || null,
          email: data.email || null,
          address: data.address || null,
          state: data.state || null,
          country: data.country || null,
        };

        const result = await vendorService.update(
          data.id,
          updateData,
          editingSnapshot || {},
          context
        );

        if (result.error) throw result.error;

        // Refetch current page to get updated data
        const refreshResult = await vendorService.listWithStatusPaginated(
          companyId,
          { page: pageIndex, pageSize },
          { search: debouncedSearch, externalSystem: selectedExternalSystem }
        );
        if (!refreshResult.error) {
          setVendors(refreshResult.data);
          setTotalCount(refreshResult.totalCount);
        }

        toast({ title: 'Success', description: 'Vendor updated successfully.' });
      } else {
        // Get default status_id for new vendors
        const defaultStatusId = await getDefaultStatusId('vendor');
        
        // Insert new vendor using service layer
        const result = await vendorService.create(
          {
            name: data.name,
            city: data.city || null,
            phone: data.phone || null,
            email: data.email || null,
            status_id: defaultStatusId,
            address: data.address || null,
            state: data.state || null,
            country: data.country || null,
          },
          context
        );

        if (result.error) throw result.error;

        // Refetch current page
        const refreshResult = await vendorService.listWithStatusPaginated(
          companyId,
          { page: pageIndex, pageSize },
          { search: debouncedSearch, externalSystem: selectedExternalSystem }
        );
        if (!refreshResult.error) {
          setVendors(refreshResult.data);
          setTotalCount(refreshResult.totalCount);
        }

        toast({ title: 'Success', description: 'Vendor created successfully.' });
      }
    } catch (error: any) {
      console.error('Error saving vendor:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save vendor.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDeleteVendor = (vendor: Vendor) => {
    setVendorToDelete(vendor);
    setDeleteDialogOpen(true);
  };

  // AG Grid column definitions
  const columnDefs = useMemo<ColDef<Vendor>[]>(() => [
    {
      field: 'name',
      headerName: 'Vendor Name',
      flex: 1,
      minWidth: 200,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<Vendor>) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <span className="font-medium">{params.value}</span>
        </div>
      ),
    },
    {
      field: 'city',
      headerName: 'City',
      width: 150,
      sortable: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 180,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<Vendor>) =>
        params.value ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="w-4 h-4" />
            {params.value}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      field: 'current_status',
      headerName: 'Status',
      width: 140,
      sortable: true,
      valueGetter: (params) => params.data?.current_status?.name,
      cellRenderer: (params: ICellRendererParams<Vendor>) =>
        params.data?.current_status ? (
          <span className="badge badge-primary">
            {params.data.current_status.name}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      colId: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams<Vendor>) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="btn-ghost p-2">
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => params.data && handleEditVendor(params.data)}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => params.data && handleDeleteVendor(params.data)}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ], []);

  const confirmDeleteVendor = async () => {
    if (!vendorToDelete || !companyId || !user) return;

    const context: ServiceContext = {
      companyId,
      userId: user.id,
    };

    try {
      // Capture the vendor data for history
      const snapshot = {
        name: vendorToDelete.name,
        city: vendorToDelete.city,
        phone: vendorToDelete.phone,
        email: vendorToDelete.email,
        address: vendorToDelete.address,
        state: vendorToDelete.state,
        country: vendorToDelete.country,
        status_id: vendorToDelete.status_id,
      };

      const result = await vendorService.delete(vendorToDelete.id, snapshot, context);
      if (result.error) throw result.error;

      // Refetch current page after deletion
      const refreshResult = await vendorService.listWithStatusPaginated(
        companyId,
        { page: pageIndex, pageSize },
        { search: debouncedSearch, externalSystem: selectedExternalSystem }
      );
      
      if (!refreshResult.error) {
        // If current page is now empty and not on first page, go to previous page
        if (refreshResult.data.length === 0 && pageIndex > 0) {
          setPageIndex(pageIndex - 1);
        } else {
          setVendors(refreshResult.data);
          setTotalCount(refreshResult.totalCount);
        }
      }

      toast({ title: 'Success', description: 'Vendor deleted successfully.' });
    } catch (error: any) {
      console.error('Error deleting vendor:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete vendor.',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
    }
  };

  const handleImportVendors = async (importedVendors: VendorImportRow[]) => {
    if (!companyId) {
      toast({
        title: 'Error',
        description: 'No company associated with your profile.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Get default status_id for new vendors
      const defaultStatusId = await getDefaultStatusId('vendor');

      // Batch insert (no history tracking for bulk import)
      const vendorsToInsert = importedVendors.map((v) => ({
        company_id: companyId,
        name: v.name,
        city: v.city || null,
        phone: v.phone || null,
        email: v.email || null,
        status_id: defaultStatusId,
        address: v.address || null,
        state: v.state || null,
        country: v.country || null,
      }));

      const { error } = await supabase
        .from('vendors')
        .insert(vendorsToInsert);

      if (error) throw error;

      // Refetch current page
      const refreshResult = await vendorService.listWithStatusPaginated(
        companyId,
        { page: pageIndex, pageSize },
        { search: debouncedSearch, externalSystem: selectedExternalSystem }
      );
      if (!refreshResult.error) {
        setVendors(refreshResult.data);
        setTotalCount(refreshResult.totalCount);
      }

      toast({
        title: 'Success',
        description: `Imported ${importedVendors.length} vendors successfully.`,
      });
    } catch (error: any) {
      console.error('Error importing vendors:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to import vendors.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Filter allVendors for export based on current search/filter
  const filteredVendorsForExport = useMemo(() => {
    return allVendors.filter((vendor) => {
      const matchesSearch =
        vendor.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (vendor.city?.toLowerCase().includes(debouncedSearch.toLowerCase()) ?? false);
      
      const matchesExternalSystem = !selectedExternalSystem || vendor.external_system === selectedExternalSystem;
      
      return matchesSearch && matchesExternalSystem;
    });
  }, [allVendors, debouncedSearch, selectedExternalSystem]);

  // Show loading state only on initial load
  if (isLoading && vendors.length === 0 && !companyId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Toolbar actions for the AGDataGrid
  const toolbarActions = (
    <div className="flex items-center gap-2">
      {/* External System Filter */}
      {externalSystems.length > 0 && (
        <Select
          value={selectedExternalSystem || "all"}
          onValueChange={handleExternalSystemChange}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All External Systems" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All External Systems</SelectItem>
            {externalSystems.map((system) => (
              <SelectItem key={system.id} value={system.name}>
                {system.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
        >
          <X className="w-4 h-4 mr-2" />
          Clear
        </Button>
      )}
    </div>
  );

  return (
    <div className="animate-slide-up">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Vendors</h1>
          <p className="page-subtitle">Manage your vendor relationships and information.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setExportDialogOpen(true)}>
            <Download className="w-5 h-5" />
            Export
          </button>
          <button className="btn-secondary" onClick={() => setImportDialogOpen(true)}>
            <Upload className="w-5 h-5" />
            Import
          </button>
          <button className="btn-primary" onClick={handleAddVendor}>
            <Plus className="w-5 h-5" />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Vendors Table with AG Grid */}
      <AGDataGrid<Vendor>
        rowData={vendors}
        columnDefs={columnDefs}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search vendors..."
        isLoading={isLoading && vendors.length === 0}
        isFetching={isLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onRowClicked={handleRowClick}
        onRowDoubleClicked={handleRowDoubleClick}
        toolbarActions={toolbarActions}
        height="calc(100vh - 320px)"
      />

      <VendorFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        vendor={editingVendor}
        onSave={handleSaveVendor}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{vendorToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteVendor} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <VendorImportWizard
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImport={handleImportVendors}
      />

      <VendorExportWizard
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        allVendors={allVendors}
        filteredVendors={filteredVendorsForExport}
        hasActiveFilter={hasActiveFilters !== null && hasActiveFilters !== undefined && hasActiveFilters !== ''}
      />
    </div>
  );
}
